import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search, Zap } from "lucide-react";
import { Countdown } from "./Countdown";
import { fmtShort } from "@/lib/format";

const HERO_IMG =
  "https://images.unsplash.com/photo-1528489290689-444dece355b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxmZXN0aXZhbCUyMGNvbmNlcnQlMjBjcm93ZCUyMGxhc2VyJTIwc3RhZ2UlMjBsaWdodHMlMjBuZW9uJTIwZGFya3xlbnwwfHx8YmxhY2t8MTc5MjA0ODM3OXww&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "NOOIT MEER", cls: "text-white" },
  { text: "TE LAAT VOOR", cls: "text-white" },
  { text: "JE KAARTJES.", cls: "text-volt" },
];

export const Hero = ({ total, liveCount, nextDrop, onSearch }) => {
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);

  const submit = (e) => {
    e.preventDefault();
    onSearch(q);
    document.getElementById("events")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-16">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-48">
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover object-center opacity-60" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-[#070709]/70 to-[#070709]/40" />
      <div className="absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-volt/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[88vh] flex items-center py-20">
        <div className="grid w-full gap-12 lg:grid-cols-[1.5fr_1fr] lg:items-end">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-volt/30 bg-volt/5 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-volt"
              data-testid="hero-stats-pill"
            >
              <Zap className="h-3 w-3" />
              Live tracking · {total} events · {liveCount} nu in verkoop
            </motion.p>

            <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
              {LINES.map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                    className={`block ${line.cls}`}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-300"
            >
              Nooit meer te laat voor jouw favoriete festival, concert of event. Wij tracken elke
              kaartverkoop en pre-sale in Nederland — tot op de seconde.
            </motion.p>

            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.6 }}
              className="mt-8 flex max-w-xl items-center gap-2 rounded-full border border-white/15 bg-black/50 p-1.5 backdrop-blur-xl focus-within:border-volt/60 transition-colors"
            >
              <Search className="ml-3 h-4 w-4 shrink-0 text-slate-500" />
              <input
                type="text"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  onSearch(e.target.value);
                }}
                placeholder="Zoek op event, artiest of stad..."
                data-testid="search-input"
                className="w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
              />
              <button
                type="submit"
                data-testid="search-submit-button"
                className="shrink-0 rounded-full bg-volt px-5 py-2.5 text-sm font-bold text-black transition-transform hover:scale-105"
              >
                Zoek
              </button>
            </motion.form>
          </div>

          {nextDrop && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl border border-white/10 bg-black/60 p-5 backdrop-blur-xl shadow-2xl"
              data-testid="hero-next-drop"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-volt">
                Eerstvolgende drop
              </p>
              <p className="mt-2 font-display text-xl font-bold tracking-tight">{nextDrop.name}</p>
              <p className="mt-1 text-xs text-slate-400">
                {nextDrop.venue} · {nextDrop.city} — {fmtShort(nextDrop.sale_start)}
              </p>
              <div className="mt-4">
                <Countdown target={nextDrop.sale_start} />
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
